import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import {
  FilePenLine,
  Forward,
  SearchX,
  Trash2,
  UserRoundSearch,
} from 'lucide-react'

import { UserDisplay } from '@/components'
import { ClickableCellWithTooltip } from '@/components/ClickableCellWithTooltip'
import { TableColumn } from '@/components/Table'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { isUserReviewer } from '@/features/view-tfl-uploaded'
import {
  DeliverableSetStatus,
  ResponseDatasetVersion,
  ResponseDeliverableSet,
} from '@/schema'
import { formatDisplayDate } from '@/utils'
import { DATE_FORMAT } from '@/utils/constants'

import { Alignment, FreezePosition } from '../../components/Table/tableHelper'

import { DatasetVersionsCell } from './components/ReportingEventsCell'
import { ReviewerList } from './components/ReviewerList'
import { validateTargetDateIsOverdue } from './utils/validation'
import { getReviewerUsers, getViewerUsers } from './utils'
import { DELIVERABLE_SET_STATUSES } from '.'

/**
 * Status styling configuration
 */
const STATUS_STYLES: Record<
  DeliverableSetStatus,
  { bg: string; text: string; border: string }
> = {
  [DeliverableSetStatus.DRAFT]: {
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
  },
  [DeliverableSetStatus.IN_REVIEW]: {
    bg: 'bg-warning-50',
    text: 'text-warning-700',
    border: 'border-warning-200',
  },
  [DeliverableSetStatus.APPROVED]: {
    bg: 'bg-success-50',
    text: 'text-success-700',
    border: 'border-success-200',
  },
  [DeliverableSetStatus.ARCHIVED]: {
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
  },
}

export type DeliverableSetAction =
  | 'edit'
  | 'delete'
  | 'view-detail'
  | 'view-files-with-open-comments'
  | 'view-total-open-comments'
  | 'sendApproval'
  | 'cancelApproval'
  | 'delegateReview'

export const getDeliverableSetListColumns = (payload: {
  handleActionClick: (
    action: DeliverableSetAction,
    item: ResponseDeliverableSet
  ) => void
  t: TFunction
  isFetching?: boolean
  currentUserId?: string
  isTeamMember?: boolean
  isOrgAdminOrProjectLead?: boolean
}): TableColumn<ResponseDeliverableSet>[] => {
  const {
    handleActionClick,
    t,
    isFetching = false,
    currentUserId,
    isTeamMember = false,
    isOrgAdminOrProjectLead = false,
  } = payload
  return [
    {
      id: 'deliverable_set_name',
      label: t('deliverableSet.columns.name'),
      width: 240,
      sortable: true,
      isFrozen: true,
      freezePosition: FreezePosition.LEFT,
      renderCell: (value, row) => (
        <ClickableCellWithTooltip
          value={typeof value === 'string' ? (value?.toString() ?? '-') : '-'}
          onClick={() => handleActionClick('view-detail', row)}
        />
      ),
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
    },
    {
      id: 'company_name',
      label: t('deliverableSet.columns.company'),
      width: 186,
      sortable: true,
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
    },
    {
      id: 'project_reporting_name',
      label: t('deliverableSet.columns.projectReportingEvent'),
      width: 300,
      sortable: true,
      renderCell: (_, row) => {
        const project = row.project_name || ''
        const reportingEvent = row.reporting_event_name || ''
        const combined =
          project && reportingEvent
            ? `${project} / ${reportingEvent}`
            : project || reportingEvent || '-'
        return <span>{combined}</span>
      },
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
    },
    {
      id: 'status',
      label: t('deliverableSet.columns.status'),
      width: 120,
      sortable: true,
      renderCell: (value) => {
        const status = value as DeliverableSetStatus
        const statusLabel =
          DELIVERABLE_SET_STATUSES[
            status as keyof typeof DELIVERABLE_SET_STATUSES
          ] || '-'
        const styles = STATUS_STYLES[status]

        if (!styles || statusLabel === '-') {
          return <span>-</span>
        }

        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full border-solid border ${styles.bg} ${styles.text} ${styles.border} text-xs font-medium`}
          >
            {statusLabel}
          </span>
        )
      },
      align: Alignment.RIGHT,
      alignLabel: Alignment.RIGHT,
    },
    {
      id: 'snapshot_tfl_total',
      label: t('deliverableSet.columns.tflFiles'),
      width: 130,
      sortable: true,
      renderCell: (value) => {
        const count = Number(value ?? 0)
        return <span>{count}</span>
      },
      align: Alignment.RIGHT,
      alignLabel: Alignment.RIGHT,
    },
    {
      id: 'snapshot_tfl_has_open_comment',
      label: t('deliverableSet.columns.filesWithOpenComments'),
      width: 250,
      renderCell: (value) => {
        const count = Number(value ?? 0)
        return (
          <span className={count > 0 ? 'text-brand-600' : ''}>{count}</span>
        )
      },
      onCellClick: (row) => {
        const count = Number(row.snapshot_tfl_has_open_comment ?? 0)
        if (count > 0) {
          handleActionClick('view-files-with-open-comments', row)
        }
      },
      align: Alignment.RIGHT,
      alignLabel: Alignment.RIGHT,
      sortable: true,
    },
    {
      id: 'review_open_date',
      label: t('deliverableSet.columns.reviewOpenDate'),
      width: 180,
      renderCell: (value) => (
        <span>
          {value && typeof value === 'string'
            ? formatDisplayDate(value, DATE_FORMAT)
            : '-'}
        </span>
      ),
      align: Alignment.RIGHT,
      alignLabel: Alignment.RIGHT,
      sortable: true,
    },
    {
      id: 'snapshot_tfl_approved',
      label: t('deliverableSet.columns.approved'),
      width: 133,
      renderCell: (value) => {
        const count = Number(value ?? 0)
        return <span>{count}</span>
      },
      align: Alignment.RIGHT,
      alignLabel: Alignment.RIGHT,
      sortable: true,
    },
    {
      id: 'snapshot_comment_open',
      label: t('deliverableSet.columns.totalOpenComments'),
      width: 220,
      renderCell: (value) => {
        const count = value !== undefined && value !== null ? Number(value) : 0
        return (
          <span className={count > 0 ? 'text-brand-600' : ''}>{count}</span>
        )
      },
      onCellClick: (row) => {
        const count = Number(row.snapshot_comment_open ?? 0)
        if (count > 0) {
          handleActionClick('view-total-open-comments', row)
        }
      },
      align: Alignment.RIGHT,
      alignLabel: Alignment.RIGHT,
      sortable: true,
    },
    {
      id: 'dataset_versions',
      label: t('deliverableSet.columns.datasetVersion'),
      width: 240,
      renderCell: (value) => {
        const versions = Array.isArray(value)
          ? (value as unknown as ResponseDatasetVersion[])
          : []
        return <DatasetVersionsCell versions={versions} />
      },
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
    },
    {
      id: 'owner',
      label: t('deliverableSet.columns.owner'),
      width: 150,
      renderCell: (_, row) => {
        return <UserDisplay user={row.owner} showAvatar={false} />
      },
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
      sortable: true,
    },
    {
      id: 'reviewers',
      label: t('deliverableSet.columns.reviewer'),
      width: 280,
      renderCell: (_, row) => {
        const { users, greyedOut } = getReviewerUsers(row)
        if (!users?.length) return <span>-</span>
        return <ReviewerList reviewers={users} greyedOut={greyedOut} />
      },
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
    },
    {
      id: 'viewers',
      label: t('deliverableSet.columns.viewer'),
      width: 280,
      renderCell: (_, row) => {
        const { users, greyedOut } = getViewerUsers(row)
        if (!users?.length) return <span>-</span>
        return <ReviewerList reviewers={users} greyedOut={greyedOut} />
      },
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
    },
    {
      id: 'due_date',
      label: t('deliverableSet.columns.targetDate'),
      width: 138,
      sortable: true,
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
      renderCell: (value) => {
        if (!value || typeof value !== 'string') return <span>-</span>
        const dueDate = formatDisplayDate(value, DATE_FORMAT)
        const isOverdue = validateTargetDateIsOverdue(value)
        return (
          <span className={isOverdue ? 'text-error-600' : 'text-primary-900'}>
            {dueDate}
          </span>
        )
      },
    },
    {
      id: 'last_activity_date',
      label: t('deliverableSet.columns.lastActivity'),
      width: 160,
      sortable: true,
      renderCell: (value) => (
        <span>
          {value && typeof value === 'string'
            ? formatDisplayDate(value, DATE_FORMAT)
            : '-'}
        </span>
      ),
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
    },
    {
      id: 'snapshot_tfl_progress',
      label: t('deliverableSet.columns.progress'),
      width: 121,
      renderCell: (value) => (
        <span>
          {value !== undefined && value !== null
            ? `${Number(value).toFixed(0)}%`
            : '-'}
        </span>
      ),
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
      sortable: true,
    },
    {
      id: 'created_by_full_name',
      label: t('deliverableSet.columns.createdBy'),
      width: 170,
      sortable: true,
      renderCell: (_, row) => {
        return <UserDisplay user={row.created_by_user} showAvatar={false} />
      },
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
    },
    {
      id: 'created_at',
      label: t('deliverableSet.columns.createdDate'),
      width: 146,
      sortable: true,
      renderCell: (value) => (
        <span>
          {value && typeof value === 'string'
            ? dayjs(value).format(DATE_FORMAT)
            : '-'}
        </span>
      ),
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
    },
    {
      id: 'actions',
      width: 144,
      isFrozen: true,
      freezePosition: FreezePosition.RIGHT,
      renderCell: (_, row) => {
        const isAllowDeleting =
          row.snapshot_tfl_total === 0 &&
          row.snapshot_tfl_has_open_comment === 0 &&
          row.snapshot_tfl_approved === 0

        const isHaveFiles = !!row.snapshot_tfl_total

        const isShowSendApproval =
          row.status === DeliverableSetStatus.DRAFT &&
          isOrgAdminOrProjectLead &&
          isHaveFiles
        const isShowCancelApproval =
          row.status === DeliverableSetStatus.IN_REVIEW &&
          !isTeamMember &&
          isHaveFiles
        const isShowDelegate =
          row.status === DeliverableSetStatus.IN_REVIEW &&
          isUserReviewer(row.reviewers, currentUserId)

        return (
          <div className="flex gap-0.5 items-center">
            {!isTeamMember && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="text"
                    className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActionClick('edit', row)
                    }}
                    disabled={isFetching}
                  >
                    <FilePenLine size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>{t('deliverableSet.actions.edit')}</span>
                </TooltipContent>
              </Tooltip>
            )}
            {isAllowDeleting && !isTeamMember && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="text"
                    className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActionClick('delete', row)
                    }}
                    disabled={isFetching}
                  >
                    <Trash2 size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>{t('deliverableSet.actions.delete')}</span>
                </TooltipContent>
              </Tooltip>
            )}
            {isShowSendApproval && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="text"
                    className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActionClick('sendApproval', row)
                    }}
                    disabled={isFetching}
                  >
                    <UserRoundSearch size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>{t('deliverableSet.actions.sendApprovalRequest')}</span>
                </TooltipContent>
              </Tooltip>
            )}
            {isShowCancelApproval && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="text"
                    className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActionClick('cancelApproval', row)
                    }}
                    disabled={isFetching}
                  >
                    <SearchX size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>
                    {t('deliverableSet.actions.cancelApprovalRequest')}
                  </span>
                </TooltipContent>
              </Tooltip>
            )}
            {isShowDelegate && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="text"
                    className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActionClick('delegateReview', row)
                    }}
                    disabled={isFetching}
                  >
                    <Forward size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>{t('deliverableSet.actions.delegateReviewer')}</span>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )
      },
    },
  ]
}
